<template>
  <div
    class="pdf_viewer_popup pdfViewMain"
    @contextmenu.prevent
  >
    <div class="viewer_body">

      <!-- ==========================================
           PDF VIEWER HEADER
      =========================================== -->
      <!-- <div class="pdf_viewer_header"></div> -->


      <!-- ==========================================
           PROFESSIONAL PDF TOOLBAR
      =========================================== -->
      <div class="custom_toolbar_nav">
        <div class="pdf-toolbar">

          <!-- =========================
               NOTES BUTTON
          ========================== -->
          <button
            type="button"
            class="toolbar-action"
            :class="{ active: pdfNotesActive }"
            @click="toggleNotes"
            title="Notes"
          >
            <svg
              width="21"
              height="21"
              viewBox="0 0 512 512"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M496.093,189.613c-18.643-15.674-47.168-13.807-63.354,5.493l-9.727,11.508l68.945,57.849l9.288-11.466 C517.22,233.997,515.199,205.621,496.093,189.613z"
              />

              <path
                fill="currentColor"
                d="M301.375,350.534l-5.131,6.072c-4.453,5.332-7.661,11.704-9.272,18.457l-13.945,58.359 c-1.318,5.522,0.601,11.323,4.951,14.971c4.234,3.558,10.206,4.591,15.601,2.285l55.063-23.877 c6.372-2.769,12.085-7.031,16.538-12.319l5.149-6.092L301.375,350.534z"
              />

              <polygon
                fill="currentColor"
                points="403.656,229.517 320.733,327.631 389.683,385.487 472.601,287.366"
              />

              <path
                fill="currentColor"
                d="M376.02,66.504l-56.982-54.141c-5.387-5.107-12.014-8.115-18.999-10.069V90h89.052 C387.23,81.09,382.69,72.836,376.02,66.504z"
              />

              <path
                fill="currentColor"
                d="M257.792,368.091c2.681-11.221,8.027-21.841,15.439-30.718l116.807-138.214V120h-105c-8.291,0-15-6.709-15-15V0h-225 c-24.814,0-45,20.186-45,45v422c0,24.814,20.186,45,45,45h300c24.814,0,45-20.186,45-45v-35.459l-1.948,2.305 c-7.368,8.775-16.875,15.85-27.466,20.465l-55.107,23.892c-15.532,6.707-33.511,4.331-46.816-6.812 c-13.14-11.03-18.838-28.242-14.854-44.941L257.792,368.091z"
              />
            </svg>

            <span class="toolbar-tooltip">
              Notes
            </span>
          </button>


          <!-- =========================
               DIVIDER
          ========================== -->
          <div class="toolbar-divider"></div>


          <!-- =========================
               APPLY CHANGES
          ========================== -->
          <button
            type="button"
            class="apply-button"
            @click="applyChanges"
            :disabled="isSubmitted"
          >
            <i
              class="fa"
              :class="
                isSubmitted
                  ? 'fa-spinner fa-spin'
                  : 'fa-check'
              "
            ></i>

            <span>
              {{ isSubmitted ? 'Saving...' : 'Apply Changes' }}
            </span>
          </button>


          <!-- =========================
               CLOSE BUTTON
          ========================== -->
          <button
            type="button"
            class="toolbar-action close-action"
            @click="closeViewer"
            title="Close"
          >
            <i
              class="fa fa-times"
              aria-hidden="true"
            ></i>

            <span class="toolbar-tooltip">
              Close
            </span>
          </button>

        </div>
      </div>


      <!-- ==========================================
           PDF CONTENT
      =========================================== -->
      <div
        class="row"
        id="container"
      >

        <!-- ========================================
             NOTES PANEL
        ========================================= -->
        <div
          v-show="showNotes"
          class="col-auto pr-0"
          id="left_panel"
          :style="{
            width: `${leftPanelWidth}px`
          }"
        >
          <pdfNotes
            :class="{ actv: pdfNotesActive }"
            :pdfIDForNote="pdfID"
          />
        </div>


        <!-- ========================================
             RESIZE HANDLE
        ========================================= -->
        <div
          v-show="showNotes"
          class="col-auto"
          id="drag"
          @mousedown="startResize"
        ></div>


        <!-- ========================================
             PDF ANNOTATION VIEWER
        ========================================= -->
        <div class="col pdf_annotation_section">
          <pdfAnnotate
            :PDFurl="pdfViewerURL"
            :pdfId="pdfID"
          />
        </div>

      </div>

    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { bus } from '@/main';
import root from '@/_helpers/roots';
import toast from '@/services/toast';

import pdfAnnotate from './pdf-render-annotation/';
import pdfNotes from './pdf-notes/';


const SAVE_ANNOTATION_URL =
  root.saveAnnotationApiUrl.link;


export default {
  name: 'PdfViewer',


  components: {
    pdfNotes,
    pdfAnnotate
  },


  props: {

    pdfViewerURL: {
      type: String,
      required: true
    },

    pdfViewerFileNAME: {
      type: String,
      default: ''
    },

    pdfID: {
      type: [String, Number],
      required: true
    }

  },


  data() {
    return {

      // Notes panel
      showNotes: false,

      pdfNotesActive: false,


      // Save state
      isSubmitted: false,


      // Notes panel width
      leftPanelWidth: 290,


      // Resize state
      isResizing: false

    };
  },


  mounted() {

    document.addEventListener(
      'mousemove',
      this.handleResize
    );

    document.addEventListener(
      'mouseup',
      this.stopResize
    );

  },


  beforeDestroy() {

    document.removeEventListener(
      'mousemove',
      this.handleResize
    );

    document.removeEventListener(
      'mouseup',
      this.stopResize
    );

  },


  methods: {

    // ==========================================
    // APPLY CHANGES
    // ==========================================

    async applyChanges() {

      const annotations =
        localStorage.getItem(
          `${this.pdfViewerURL}/annotations`
        );

      this.isSubmitted = true;


      try {

        await axios.post(
          SAVE_ANNOTATION_URL,
          {
            paper_id: this.pdfID,
            annotation_data: annotations
          }
        );


        toast.success(
          'Request processed successfully.'
        );


        this.closeViewer();

      } catch (err) {

        console.error(
          'Save annotation error:',
          err
        );

        toast.error(
          'Request Failed'
        );

      } finally {

        this.isSubmitted = false;

      }

    },


    // ==========================================
    // START RESIZE
    // ==========================================

    startResize() {

      this.isResizing = true;

      document.body.style.cursor = 'w-resize';
      document.body.style.userSelect = 'none';

    },


    // ==========================================
    // HANDLE RESIZE
    // ==========================================

    handleResize(e) {

      if (!this.isResizing) {
        return;
      }


      const screenDistance =
        window.outerWidth - e.clientX;


      if (
        e.clientX > 290 &&
        screenDistance > 750
      ) {

        this.leftPanelWidth =
          e.clientX;

      }

    },


    // ==========================================
    // STOP RESIZE
    // ==========================================

    stopResize() {

      this.isResizing = false;

      document.body.style.cursor = '';
      document.body.style.userSelect = '';

    },


    // ==========================================
    // TOGGLE NOTES
    // ==========================================

    toggleNotes() {

      /*
       * Switch PDF annotation cursor
       * back to default when opening notes.
       */
      const cursorBtn =
        document.querySelector(
          '.toolbar .cursor'
        );


      if (cursorBtn) {
        cursorBtn.click();
      }


      if (this.showNotes) {

        // Close notes
        this.showNotes = false;


        setTimeout(() => {

          this.pdfNotesActive = false;

        }, 10);

      } else {

        // Open notes
        this.leftPanelWidth = 290;

        this.showNotes = true;


        setTimeout(() => {

          this.pdfNotesActive = true;

        }, 10);

      }

    },


    // ==========================================
    // FETCH PAPER LIST
    // ==========================================

    fetchPaperList() {

      const bucketId =
        this.$route.params.bucketId;


      if (bucketId !== undefined) {

        this.$store.dispatch(
          'getBucketDetailsByIdOfAwerackFun',
          {
            id: bucketId
          }
        );

      } else {

        this.$store.dispatch(
          'getAllPaperPileFun'
        );

      }

    },


    // ==========================================
    // CLOSE VIEWER
    // ==========================================

    closeViewer() {

      /*
       * Reset PDF cursor
       */
      const cursorBtn =
        document.querySelector(
          '.toolbar .cursor'
        );


      if (cursorBtn) {
        cursorBtn.click();
      }


      /*
       * Refresh paper list
       */
      this.fetchPaperList();


      /*
       * Close popup
       */
      bus.$emit(
        'pdfViewerPopupBus',
        false
      );


      /*
       * Restore body scrolling
       */
      document.body.classList.remove(
        'overflow-hidden',
        'pdf_view_actv'
      );

    }

  }

};
</script>


<style scoped lang="scss">

/* ==================================================
   PDF VIEWER OVERLAY
================================================== */

.pdf_viewer_popup {

  position: fixed;

  inset: 0;

  z-index: 1000;

  background-color:
    rgba(0, 0, 0, 0.85);

}


/* ==================================================
   VIEWER BODY
================================================== */

.viewer_body {

  position: relative;

  width: 100%;

  height: 100%;

}


/* ==================================================
   PDF HEADER
================================================== */

.pdf_viewer_header {

  position: absolute;

  top: 0;

  left: 0;

  right: 0;

  height: 45px;

  background: #1f1f1f;

  z-index: 1002;

}


/* ==================================================
   PROFESSIONAL TOOLBAR
================================================== */

.custom_toolbar_nav {
    position: fixed;
    top: 8px;
    left: 18px;
    z-index: 10001;
}


/* ==================================================
   TOOLBAR CONTAINER
================================================== */

.pdf-toolbar {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  background: rgba(30, 32, 34, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35), 0 2px 6px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
}


/* ==================================================
   COMMON TOOLBAR ACTION
================================================== */

.toolbar-action {

  position: relative;

  width: 38px;

  height: 38px;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: 0;

  border:
    1px solid
    transparent;

  border-radius: 6px;

  background: transparent;

  color: #c9cdd3;

  cursor: pointer;

  outline: none;

  transition:
    background 0.18s ease,
    color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;


  &:hover {

    background:
      rgba(255, 255, 255, 0.09);

    color: #ffffff;

    border-color:
      rgba(255, 255, 255, 0.08);

  }


  &:active {

    transform:
      scale(0.94);

  }


  &:focus {

    outline: none;

    box-shadow: none;

  }


  svg {

    display: block;

    transition:
      color 0.18s ease;

  }

}


/* ==================================================
   NOTES ACTIVE STATE
================================================== */

.toolbar-action.active {

  background:
    rgba(255, 193, 7, 0.14);

  border-color:
    rgba(255, 193, 7, 0.35);

  color: #ffc107;


  &:hover {

    background:
      rgba(255, 193, 7, 0.20);

    color: #ffd54f;

  }

}


/* ==================================================
   TOOLBAR DIVIDER
================================================== */

.toolbar-divider {

  width: 1px;

  height: 28px;

  margin:
    0 3px;

  background:
    rgba(255, 255, 255, 0.12);

}


/* ==================================================
   APPLY CHANGES BUTTON
================================================== */

.apply-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding:
    0 15px;
  border:
    1px solid
    rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: #b3261e;
  color: #ffffff;
  font-size: 14px;
  font-weight: normal;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  outline: none;
  box-shadow:
    0 2px 5px
    rgba(0, 0, 0, 0.20);
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;

  &:hover:not(:disabled) {

    background: #d32f2f;

    border-color:
      rgba(255, 255, 255, 0.20);

    box-shadow:
      0 4px 10px
      rgba(0, 0, 0, 0.25);

  }


  &:active:not(:disabled) {

    transform:
      translateY(1px);

  }


  &:focus {

    outline: none;

    box-shadow:
      0 2px 5px
      rgba(0, 0, 0, 0.20);

  }


  &:disabled {

    opacity: 0.65;

    cursor: not-allowed;

  }


  i {

    font-size: 13px;

  }

}


/* ==================================================
   CLOSE BUTTON
================================================== */

.close-action {

  color: #ff6b6b;


  &:hover {

    color: #ffffff;

    background:
      rgba(220, 53, 69, 0.20);

    border-color:
      rgba(220, 53, 69, 0.30);

  }


  .fa-times {

    font-size: 18px;

  }

}


/* ==================================================
   TOOLTIP
================================================== */

.toolbar-tooltip {

  position: absolute;

  top:
    calc(100% + 9px);

  left: 50%;

  transform:
    translateX(-50%)
    translateY(-3px);

  padding:
    5px 8px;

  border-radius: 4px;

  background: #111418;

  border:
    1px solid
    rgba(255, 255, 255, 0.10);

  color: #ffffff;

  font-size: 11px;

  font-weight: 500;

  white-space: nowrap;

  opacity: 0;

  visibility: hidden;

  pointer-events: none;

  box-shadow:
    0 4px 12px
    rgba(0, 0, 0, 0.30);

  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    visibility 0.15s ease;

}


/* Show tooltip */

.toolbar-action:hover
.toolbar-tooltip {

  opacity: 1;

  visibility: visible;

  transform:
    translateX(-50%)
    translateY(0);

}


/* ==================================================
   PDF CONTENT
================================================== */

#container {

  width: 100%;

  height: 100%;

  margin: 0;

  padding: 0px;

}


/* ==================================================
   NOTES PANEL
================================================== */

#left_panel {

  height: 100%;

  overflow: hidden;

}


/* ==================================================
   RESIZE HANDLE
================================================== */

#drag {

  padding: 0;

  width: 5px;

  height: 100%;

  cursor: w-resize;

  background:
    #a6ec9d;

  user-select: none;

  transition:
    background 0.15s ease;


  &:hover {

    background:
      #69d65e;

  }

}


/* ==================================================
   PDF ANNOTATION SECTION
================================================== */

.pdf_annotation_section {

  height: 100%;

  min-width: 0;

  overflow: hidden;

}


/* ==================================================
   MOBILE
================================================== */

@media only screen and (max-width: 767px) {

  .custom_toolbar_nav {

    top: 8px;

    right: 8px;

  }


  .pdf-toolbar {

    min-height: 46px;

    padding: 5px;

    gap: 4px;

    border-radius: 7px;

  }


  .toolbar-action {

    width: 36px;

    height: 36px;

  }


  .apply-button {

    height: 36px;

    padding:
      0 11px;


    span {

      display: none;

    }


    i {

      font-size: 14px;

    }

  }


  .toolbar-divider {

    height: 24px;

    margin:
      0 2px;

  }

}


/* ==================================================
   SMALL MOBILE
================================================== */

@media only screen and (max-width: 400px) {

  .custom_toolbar_nav {

    right: 5px;

    top: 6px;

  }


  .pdf-toolbar {

    gap: 2px;

    padding: 4px;

  }


  .toolbar-action {

    width: 34px;

    height: 34px;

  }


  .apply-button {

    width: 36px;

    padding: 0;

  }

}

</style>